import { RequireAuth, RequirePermission, useAuth } from "@roastery/auth";
import { useTranslation } from "@roastery/i18n";
import { Button, ConfirmDialogProvider } from "@roastery/ui";
import { LegalLinks } from "../components/legal-links";
import { UiLabels } from "../components/ui-labels";
import { Outlet, createRootRoute, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { AdminHeader } from "../components/admin-header";
import { AdminSidebar } from "../components/admin-sidebar";
import { FullScreenMessage } from "../components/full-screen-message";

export const Route = createRootRoute({
  component: AdminLayout,
  notFoundComponent: NotFound,
});

/**
 * Sign in, then check that the person who signed in is one of us.
 *
 * <p>Both frontends talk to the same realm, so a roastery's owner can reach this address with a
 * perfectly valid token. They get a sentence and a way out rather than an empty screen: without the
 * platform permission every call under `/api/v1/platform/**` would answer 403, and a panel that
 * renders itself only to fill with refusals is worse than one that says so up front. The guard that
 * matters is on the server; this one only decides what to draw.
 */
function AdminLayout() {
  const { t } = useTranslation();
  const auth = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // The one address outside the gate (US-23.6): a page that says "signed out" is worthless if
  // reaching it requires signing in. The portal solved this with a pathless layout in US-24.4; here
  // one route does not justify renaming sixteen files.
  if (pathname === "/signed-out") {
    return <Outlet />;
  }

  const signOut = (
    <Button variant="outline" data-testid="sign-out" onClick={() => void auth.signoutRedirect()}>
      {t("header.signOut")}
    </Button>
  );

  return (
    <RequireAuth
      pending={<FullScreenMessage title={t("auth.signingIn")} testId="auth-pending" />}
      onError={(error) => (
        <FullScreenMessage
          testId="auth-error"
          title={t("auth.error")}
          description={error.message}
          action={
            <Button onClick={() => void auth.signinRedirect()} data-testid="retry-sign-in">
              {t("auth.signIn")}
            </Button>
          }
        />
      )}
    >
      <RequirePermission
        permissions={["platform.tenants.manage"]}
        pending={<FullScreenMessage title={t("app.loading")} testId="admin-pending" />}
        fallback={
          <FullScreenMessage
            testId="admin-forbidden"
            title={t("platform.forbidden.title")}
            description={t("platform.forbidden.description")}
            action={signOut}
          />
        }
      >
        <AdminShell />
      </RequirePermission>
    </RequireAuth>
  );
}

function AdminShell() {
  // Open only on a phone: from md up the sidebar is static and this state never shows.
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    // One confirmation dialog for the whole panel (US-23.6), the same as the other two applications:
    // asking "are you sure?" is a promise any screen can await rather than a browser box.
    <UiLabels>
      <ConfirmDialogProvider>
        <div className="flex h-full" data-testid="admin-shell">
          <AdminSidebar open={menuOpen} onClose={() => setMenuOpen(false)} />
          <div className="flex min-w-0 flex-1 flex-col">
            <AdminHeader onOpenMenu={() => setMenuOpen(true)} />
            <main className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="admin-main">
              <Outlet />
            </main>
            {/* The operator panel has readers too: an operator is a person whose data somebody
                processes (US-37.9, L-11). One footer, the same two documents as everywhere else. */}
            <footer className="border-t border-border px-4 py-3 md:px-6" data-testid="admin-footer">
              <LegalLinks testId="admin-legal" />
            </footer>
          </div>
        </div>
      </ConfirmDialogProvider>
    </UiLabels>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return <FullScreenMessage testId="not-found" title="404" description={t("platform.shell.notFound")} />;
}
